import React, { useState } from 'react';
import styled from 'styled-components';
import { useRegistration } from '../../../contexts/RegistrationContext';

const StepContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const Title = styled.h2`
  color: #000;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const Description = styled.p`
  color: #666;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #000;
  font-size: 0.9rem;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 15px;
  background: #f8f9fa;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 10px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
  }
`;

const RadioInput = styled.input`
  margin-top: 0.2rem;
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionTitle = styled.div`
  font-weight: 500;
  color: #000;
  margin-bottom: 0.25rem;
`;

const OptionDescription = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 15px;
  background: #f8f9fa;
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 10px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &.primary {
    background: #000;
    color: white;
    
    &:hover:not(:disabled) {
      background: #333;
      transform: translateY(-1px);
    }
  }
  
  &.secondary {
    background: #6c757d;
    color: white;
    
    &:hover:not(:disabled) {
      background: #5a6268;
      transform: translateY(-1px);
    }
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const Charter: React.FC = () => {
  const { registrationData, setCurrentStep, saveCharter } = useRegistration();
  
  const parsedPreferences = registrationData.charter_preferences 
    ? JSON.parse(registrationData.charter_preferences)
    : {};

  const [decisionMaking, setDecisionMaking] = useState(registrationData.decision_making || '');
  const [preferences, setPreferences] = useState<Record<string, boolean>>(parsedPreferences);
  const [isLoading, setIsLoading] = useState(false);

  const handlePreferenceChange = (key: string, checked: boolean) => {
    setPreferences(prev => ({ ...prev, [key]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await saveCharter({
        decision_making: decisionMaking,
        charter_preferences: JSON.stringify(preferences),
      });
      setCurrentStep(5);
    } catch (error) {
      console.error('Failed to save charter details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(3);
  };

  return (
    <StepContainer>
      <Title>Step 4: Charter Details Customization</Title>
      <Description>
        Customize your company charter by selecting your preferred options for decision-making 
        and other corporate governance aspects.
      </Description>
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Decision Making Process (Required) *</Label>
          <RadioGroup>
            <RadioOption>
              <RadioInput
                type="radio"
                name="decision_making"
                value="unanimous"
                checked={decisionMaking === 'unanimous'}
                onChange={(e) => setDecisionMaking(e.target.value)}
                required
              />
              <OptionContent>
                <OptionTitle>Unanimous Decision</OptionTitle>
                <OptionDescription>
                  All shareholders must agree for major decisions. Provides maximum protection 
                  for minority shareholders but may slow decision-making.
                </OptionDescription>
              </OptionContent>
            </RadioOption>
            
            <RadioOption>
              <RadioInput
                type="radio"
                name="decision_making"
                value="qualified_majority"
                checked={decisionMaking === 'qualified_majority'}
                onChange={(e) => setDecisionMaking(e.target.value)}
                required
              />
              <OptionContent>
                <OptionTitle>Qualified Majority (75%)</OptionTitle>
                <OptionDescription>
                  Decisions require 75% shareholder approval. Balances protection with 
                  operational efficiency for most business decisions.
                </OptionDescription>
              </OptionContent>
            </RadioOption>
            
            <RadioOption>
              <RadioInput
                type="radio"
                name="decision_making"
                value="simple_majority"
                checked={decisionMaking === 'simple_majority'}
                onChange={(e) => setDecisionMaking(e.target.value)}
                required
              />
              <OptionContent>
                <OptionTitle>Simple Majority (50%+)</OptionTitle>
                <OptionDescription>
                  Decisions require more than 50% approval. Enables faster decision-making 
                  but provides less protection for minority shareholders.
                </OptionDescription>
              </OptionContent>
            </RadioOption>
          </RadioGroup>
        </FormGroup>

        <FormGroup>
          <Label>Additional Charter Preferences</Label>
          <CheckboxGroup>
            <CheckboxOption>
              <input
                type="checkbox"
                checked={preferences.allow_electronic_meetings || false}
                onChange={(e) => handlePreferenceChange('allow_electronic_meetings', e.target.checked)}
              />
              <OptionContent>
                <OptionTitle>Allow Electronic Meetings</OptionTitle>
                <OptionDescription>
                  Enable shareholders to participate in meetings via video conference or other electronic means.
                </OptionDescription>
              </OptionContent>
            </CheckboxOption>

            <CheckboxOption>
              <input
                type="checkbox"
                checked={preferences.require_board_approval || false}
                onChange={(e) => handlePreferenceChange('require_board_approval', e.target.checked)}
              />
              <OptionContent>
                <OptionTitle>Require Board Approval for Major Transactions</OptionTitle>
                <OptionDescription>
                  Require board of directors approval for transactions above a certain threshold.
                </OptionDescription>
              </OptionContent>
            </CheckboxOption>

            <CheckboxOption>
              <input
                type="checkbox"
                checked={preferences.preemptive_rights || false}
                onChange={(e) => handlePreferenceChange('preemptive_rights', e.target.checked)}
              />
              <OptionContent>
                <OptionTitle>Preemptive Rights</OptionTitle>
                <OptionDescription>
                  Give existing shareholders the right to purchase new shares before they are offered to others.
                </OptionDescription>
              </OptionContent>
            </CheckboxOption>

            <CheckboxOption>
              <input
                type="checkbox"
                checked={preferences.drag_along_rights || false}
                onChange={(e) => handlePreferenceChange('drag_along_rights', e.target.checked)}
              />
              <OptionContent>
                <OptionTitle>Drag-Along Rights</OptionTitle>
                <OptionDescription>
                  Allow majority shareholders to force minority shareholders to sell in certain situations.
                </OptionDescription>
              </OptionContent>
            </CheckboxOption>

            <CheckboxOption>
              <input
                type="checkbox"
                checked={preferences.tag_along_rights || false}
                onChange={(e) => handlePreferenceChange('tag_along_rights', e.target.checked)}
              />
              <OptionContent>
                <OptionTitle>Tag-Along Rights</OptionTitle>
                <OptionDescription>
                  Allow minority shareholders to join when majority shareholders sell their shares.
                </OptionDescription>
              </OptionContent>
            </CheckboxOption>
          </CheckboxGroup>
        </FormGroup>

        <ButtonGroup>
          <Button type="button" className="secondary" onClick={handleBack}>
            Back: Owners Information
          </Button>
          <Button type="submit" className="primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Next: Additional Services'}
          </Button>
        </ButtonGroup>
      </Form>
    </StepContainer>
  );
};

export default Charter;